import { CheckCircle2 } from "lucide-react";
import { motion } from "framer-motion";
import { springs } from "@/components/ui/motion";
import { Button } from "@/components/ui/primitives";
import { useNavigate, useParams } from "@tanstack/react-router";
import { useTranslation } from "@/packages/utils";
import type { Id } from "@/packages/types";

// ── OnboardingStep (internal) ───────────────────────────────────

interface OnboardingStepProps {
  step: number;
  title: string;
  description: string;
  buttonLabel: string;
  isComplete: boolean;
  onClick: () => void;
  disabled?: boolean;
}

function OnboardingStep({
  step,
  title,
  description,
  buttonLabel,
  isComplete,
  onClick,
  disabled,
}: OnboardingStepProps) {
  return (
    <div
      className={`bg-surface rounded-xl border border-border/50 p-4 ${
        isComplete ? "border-l-2 border-l-green-500" : ""
      }`}
    >
      <div className="flex items-start gap-3">
        <div className="shrink-0 mt-0.5">
          {isComplete ? (
            <div className="size-8 rounded-full bg-green-500/10 flex items-center justify-center">
              <CheckCircle2 className="size-4 text-green-500" />
            </div>
          ) : (
            <div className="size-8 rounded-full bg-accent/10 text-accent flex items-center justify-center text-sm font-semibold">
              {step}
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-primary">{title}</p>
          <p className="text-xs text-muted mt-0.5">{description}</p>
          <div className="mt-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={onClick}
              disabled={disabled}
            >
              {buttonLabel}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── DashboardOnboarding ─────────────────────────────────────────

interface DashboardOnboardingProps {
  onboarding: {
    hasAudits: boolean;
    hasDocuments: boolean;
    hasChecklistActivity: boolean;
    firstAuditId: Id<"audits"> | null;
  };
  onOpenWizard: () => void;
  onOpenUpload: () => void;
}

export function DashboardOnboarding({
  onboarding,
  onOpenWizard,
  onOpenUpload,
}: DashboardOnboardingProps) {
  const { t } = useTranslation("dashboard");
  const navigate = useNavigate();
  const { workspaceSlug } = useParams({ strict: false });

  const steps = [
    {
      step: 1,
      title: t("onboarding.steps.createAudit.title"),
      description: t("onboarding.steps.createAudit.description"),
      buttonLabel: t("onboarding.steps.createAudit.button"),
      isComplete: onboarding.hasAudits,
      onClick: onOpenWizard,
    },
    {
      step: 2,
      title: t("onboarding.steps.uploadDocuments.title"),
      description: t("onboarding.steps.uploadDocuments.description"),
      buttonLabel: t("onboarding.steps.uploadDocuments.button"),
      isComplete: onboarding.hasDocuments,
      onClick: onOpenUpload,
    },
    {
      step: 3,
      title: t("onboarding.steps.editChecklist.title"),
      description: t("onboarding.steps.editChecklist.description"),
      buttonLabel: t("onboarding.steps.editChecklist.button"),
      isComplete: onboarding.hasChecklistActivity,
      disabled: onboarding.firstAuditId === null,
      onClick: () => {
        if (onboarding.firstAuditId) {
          navigate({
            to: "/$workspaceSlug/audits/$auditId",
            params: {
              workspaceSlug: workspaceSlug ?? "",
              auditId: onboarding.firstAuditId,
            },
            search: { tab: "checklist" },
          });
        }
      },
    },
  ];

  return (
    <div className="flex items-center justify-center flex-1 min-h-0">
      <motion.div
        className="max-w-[520px] w-full bg-card rounded-2xl shadow-sm border border-border/50 p-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={springs.smooth}
      >
        <h2 className="text-xl font-bold text-primary">
          {t("onboarding.welcome")}
        </h2>
        <p className="text-sm text-muted mt-1 mb-6">
          {t("onboarding.startInThreeSteps")}
        </p>

        <div className="flex flex-col">
          {steps.map((stepProps, index) => (
            <div key={stepProps.step}>
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ ...springs.smooth, delay: index * 0.08 }}
              >
                <OnboardingStep {...stepProps} />
              </motion.div>
              {index < steps.length - 1 && (
                <div className="flex justify-center">
                  <div className="h-4 border-l-2 border-dashed border-border/30" />
                </div>
              )}
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  );
}
